import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorTranslationService } from '@app/shared/errors/error-translation.service';
import { ErrorModel } from '@app/shared/errors/error.models';
import { HttpErrorNotifierService } from '@app/shared/http-service/http-error-notifier.service';
import { LastApiRequestService } from '@app/shared/http-service/last-api-request.service';
import { LoaderService } from '@app/shared/loader/loader.service';
import { Constants } from '@app/shared/models/constants';
import { RouteConstants } from '@app/shared/models/route-constants';
import { AuthService } from '@app/shared/services/auth.service';
import { ObjectUtils } from '@app/shared/utilities/object.utils';
import { StringUtils } from '@app/shared/utilities/string.utils';
import { Observable } from 'rxjs';

@Injectable()
export class HttpAppInterceptor implements HttpInterceptor {

	private requests: HttpRequest<any>[] = [];
	private _authService: AuthService;

	constructor (
		private readonly loaderService: LoaderService,
		private readonly lastApiRequestService: LastApiRequestService,
		private readonly router: Router,
		private readonly httpErrorNotifierService: HttpErrorNotifierService,
		private readonly errorTranslationService: ErrorTranslationService,
		private readonly injector: Injector
	) { }

	// Defer the injection of AuthService to avoid circular dependency issues
	private get authService (): AuthService {
		if (!this._authService) {
			this._authService = this.injector.get(AuthService);
		}
		return this._authService;
	}

	intercept (req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

		this.requests.push(req);

		this.loaderService.isLoading.next(true);
		this.lastApiRequestService.setNow();
		return new Observable(observer => {
			const subscription = next.handle(req)
				.subscribe({
					next: event => {
						if (event instanceof HttpResponse) {
							this.removeRequest(req);
							observer.next(event);
						}
					},
					error: err => {
						this.handleError(err, req);
						this.removeRequest(req);
						observer.error(err);
					},
					complete: () => {
						this.removeRequest(req);
						observer.complete();
					}
				});
			return () => {
				this.removeRequest(req);
				subscription.unsubscribe();
			};
		});
	}

	private removeRequest (req: HttpRequest<any>) {
		const i = this.requests.indexOf(req);
		if (i >= 0) {
			this.requests.splice(i, 1);
		}
		this.loaderService.isLoading.next(this.requests.length > 0);
	}

	private handleError (err: any, req: HttpRequest<any>): void {
		if (this.shouldSkipInterceptor(req)) {
			return;
		}
		this.translateErrors(err);
		this.handleHttpStatusError(err);
	}

	private translateErrors (err: any) {
		const errorObject: ErrorModel = ObjectUtils.findObjectWithKey(err, 'errorCode');
		if (errorObject) {
			this.errorTranslationService.translateError(errorObject);
		}
	}

	private shouldSkipInterceptor (req: HttpRequest<any>): boolean {
		return StringUtils.parseBoolean(req.headers.get(Constants.SkipInterceptorHeader));
	}

	private handleHttpStatusError (err: any): void {
		if (err.status === HttpStatusCode.Unauthorized) {
			this.handleUnauthorizedError(err);
		} else if (err.status === HttpStatusCode.Forbidden) {
			this.handleForbiddenError(err);
		}
	}

	private shouldForceLogout (err: any): boolean {
		return StringUtils.parseBoolean(err.headers.get(Constants.ForceLogoutHeader));
	}

	private handleUnauthorizedError (err: any): void {
		if (!this.shouldForceLogout(err)) {
			if (this.authService.checkIfTokenIsExpiredAndLogOut()) {
				return;
			}
			this.router.navigate([RouteConstants.Error401]);
		}
		this.httpErrorNotifierService.onHttpError.next(err);
	}

	private handleForbiddenError (err: any): void {
		this.router.navigate([RouteConstants.Error403]);
		this.httpErrorNotifierService.onHttpError.next(err);
	}
}